import { useState, useEffect } from "react";
import { basicInfoStore, type AssocBasicInfo } from "../../utils/basicInfoStore";
import { useAdminData } from "../../AdminDataContext";
import { getUserFacingMessage, reportError } from "../../../../api/errors";
import {
  assocBasicInfoFromApi,
  fetchTenantProfile,
  putTenantProfile,
  tenantProfileDtoFromAssoc,
} from "../../../../api/tenantProfile";
import { uploadTenantImageFile } from "../../../../api/tenantImageUpload";
import ChinaRegionPicker from "../../../../components/ChinaRegionPicker";
import { composeChinaAddress, regionFromNames, type ChinaRegionValue } from "../../../../utils/chinaAddress";

export default function BasicInfo() {
  const { tenantLegacyId } = useAdminData();
  const [info, setInfo] = useState<AssocBasicInfo>(() => basicInfoStore.get());
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [sealPreview, setSealPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    void fetchTenantProfile(tenantLegacyId)
      .then((d) => {
        if (cancelled) return;
        const next = assocBasicInfoFromApi(d);
        setInfo(next);
        basicInfoStore.save(next);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(getUserFacingMessage(e, "协会资料加载失败，请刷新。"));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const handleChange = (field: keyof AssocBasicInfo, value: string | number) => {
    setInfo((p) => ({ ...p, [field]: value }));
  };

  const handleSave = () => {
    void (async () => {
      setSaving(true);
      try {
        const logoUrl = (logoPreview ?? info.logoUrl).trim();
        const sealUrl = (sealPreview ?? info.enrollmentAgreementSealUrl).trim();
        const toSave: AssocBasicInfo = { ...info, logoUrl, enrollmentAgreementSealUrl: sealUrl };
        const row = await putTenantProfile(tenantLegacyId, tenantProfileDtoFromAssoc(toSave));
        const next = assocBasicInfoFromApi(row);
        setInfo(next);
        basicInfoStore.save(next);
        setLogoPreview(null);
        setSealPreview(null);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch (e) {
        reportError(e, "保存失败");
      } finally {
        setSaving(false);
      }
    })();
  };

  const handleSealChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadTenantImageFile(file, tenantLegacyId, "enrollmentAgreementSeal");
      setSealPreview(url);
    } catch (err) {
      reportError(err, "电子章上传失败");
    }
    e.target.value = "";
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadTenantImageFile(file, tenantLegacyId, "tenantLogo");
      setLogoPreview(url);
    } catch (err) {
      reportError(err, "Logo 上传失败");
    }
    e.target.value = "";
  };

  const Field = ({
    label, field, type = "text", placeholder, multiline = false, hint,
  }: {
    label: string;
    field: keyof AssocBasicInfo;
    type?: string;
    placeholder?: string;
    multiline?: boolean;
    hint?: string;
  }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}
        {hint && <span className="ml-1.5 text-gray-400 font-normal">{hint}</span>}
      </label>
      {multiline ? (
        <textarea
          value={String(info[field])}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          rows={3}
          maxLength={500}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors resize-none"
        ></textarea>
      ) : (
        <input
          type={type}
          value={String(info[field])}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
        />
      )}
    </div>
  );

  const NumberField = ({
    label,
    field,
    placeholder,
    hint,
    min = 0,
    max,
  }: {
    label: string;
    field: keyof AssocBasicInfo;
    placeholder?: string;
    hint?: string;
    min?: number;
    max?: number;
  }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}
        {hint && <span className="ml-1.5 text-gray-400 font-normal">{hint}</span>}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        value={String(info[field] ?? 0)}
        onChange={(e) => handleChange(field, Number(e.target.value ?? 0) || 0)}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
      />
    </div>
  );

  const logoDisplay = logoPreview || info.logoUrl;
  const sealDisplay = sealPreview || info.enrollmentAgreementSealUrl;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-gray-800">协会基本信息</h3>
          <p className="text-xs text-gray-400 mt-0.5">配置协会名称、简介、联系方式及品牌形象（已对接服务端）</p>
          {loadError && (
            <p className="text-xs text-amber-600 mt-1">加载失败：{loadError}（可使用下方本地默认值编辑后保存）</p>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 ${
            saved ? "bg-green-500 text-white" : "bg-red-600 text-white hover:bg-red-700"
          }`}
        >
          <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          {saving ? "保存中…" : saved ? "已保存" : "保存信息"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Logo & Brand */}
        <div className="col-span-1 space-y-4">
          {/* Logo Upload */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-3">协会 Logo</p>
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-200">
                {logoDisplay ? (
                  <img src={logoDisplay} alt="logo" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <i className="ri-image-add-line text-2xl text-gray-300"></i>
                    <span className="text-xs text-gray-400">上传 Logo</span>
                  </div>
                )}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                <span className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors">
                  <i className="ri-upload-line"></i> 上传图片
                </span>
              </label>
              <p className="text-xs text-gray-400 text-center">建议尺寸 200×200px<br />支持 PNG / JPG / SVG</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-1">入会协议 PDF 电子章</p>
            <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
              用于会员审核下载的协议 PDF：与会员签名、签署时间同排展示。建议透明底 PNG，方形或圆形章面。
            </p>
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-200">
                {sealDisplay ? (
                  <img src={sealDisplay} alt="电子章" className="w-full h-full object-contain p-1" />
                ) : (
                  <div className="flex flex-col items-center gap-1 px-2">
                    <i className="ri-seal-line text-2xl text-gray-300"></i>
                    <span className="text-xs text-gray-400 text-center">未上传</span>
                  </div>
                )}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={handleSealChange} />
                <span className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors">
                  <i className="ri-upload-line"></i> 上传电子章
                </span>
              </label>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-3">展示数据</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  注册会员数 <span className="text-gray-400 font-normal">（与会员库同步，只读）</span>
                </label>
                <input
                  type="number"
                  readOnly
                  value={info.memberCount}
                  className="w-full border border-gray-100 rounded-xl px-3 py-2.5 text-sm bg-gray-50 text-gray-700 cursor-default"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">成立时间</label>
                <input
                  type="date"
                  value={info.founded}
                  onChange={(e) => handleChange("founded", e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Info Forms */}
        <div className="col-span-2 space-y-4">
          {/* Basic */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-building-2-line text-red-500"></i> 协会名称
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="全称" field="name" placeholder="例：某某市球迷协会" />
              <Field label="简称" field="shortName" placeholder="例：某某球迷会" />
            </div>
            <div className="mt-4">
              <Field label="首页简要口号" field="slogan" placeholder="如：热血同行 · 并肩逐梦" hint="（显示在手机端首页欢迎条）" />
            </div>
            <div className="mt-4">
              <Field label="协会简介" field="description" multiline placeholder="简短介绍协会的背景和定位..." />
            </div>
            <div className="mt-4">
              <Field label="协会使命宣言" field="mission" multiline placeholder="协会的核心使命或口号..." />
            </div>
          </div>

          {/* 入会/续费收款（对公） */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-bank-line text-red-500"></i> 入会 / 续费收款信息
            </p>
            <p className="text-xs text-gray-400 mb-3 -mt-2">
              将用于会员审核凭证核对、续费弹窗与会员端入会缴费说明；未填写时引导用户通过上方联系方式索取账户。
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="收款户名" field="enrollmentPayee" placeholder="对公账户名称" />
              <Field label="开户银行" field="enrollmentBankName" placeholder="例：中国工商银行××支行" />
              <Field label="银行账号" field="enrollmentBankAccount" placeholder="账号" />
              <Field
                label="转账备注说明"
                field="enrollmentTransferNote"
                placeholder="例：姓名 + 入会缴费；留空则前台用默认"
                hint="（可选）"
              />
            </div>
            <div className="col-span-2 mt-4 space-y-3 rounded-xl border border-gray-100 bg-slate-50/90 p-4">
              <p className="text-xs font-semibold text-gray-700">会员申请 · 会费收取方式</p>
              <p className="text-xs text-gray-500">
                选择「在线支付」时，小程序入会流程将增加一步微信支付（按所选等级年费）。服务商 WeChatPay 参数在服务器 appsettings 中配置；各租户的微信支付子商户号（mchid）由运营平台在租户信息中维护。
              </p>
              <div className="flex flex-col gap-2.5 text-sm text-gray-800">
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={info.enrollmentFeeCollectMode === 0}
                    onChange={() => handleChange("enrollmentFeeCollectMode", 0)}
                  />
                  <span>上传银行转账凭证（默认，与现有流程一致）</span>
                </label>
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={info.enrollmentFeeCollectMode === 1}
                    onChange={() => handleChange("enrollmentFeeCollectMode", 1)}
                  />
                  <span>微信小程序在线支付（服务商 + 子商户号）</span>
                </label>
              </div>
            </div>
          </div>

          {/* 内容发布限制 */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-red-500"></i> 资讯媒体上传限制
            </p>
            <p className="text-xs text-gray-400 mb-3 -mt-2 leading-relaxed">
              该限制会同时应用于小程序端「创建资讯」的图片/视频上传（前置校验 + 服务端校验）。填 0 表示使用系统默认限制。
            </p>
            <div className="grid grid-cols-2 gap-4">
              <NumberField
                label="单张图片上限（MB）"
                field="newsImageUploadMaxMb"
                hint="0=默认（当前为 15MB）"
                min={0}
                max={50}
              />
              <NumberField
                label="单个视频上限（MB）"
                field="newsVideoUploadMaxMb"
                hint="0=默认（不超过系统总上限）"
                min={0}
                max={500}
              />
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-contacts-line text-red-500"></i> 联系方式
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="联系电话" field="phone" type="tel" placeholder="0796-XXXXXXXX" />
              <Field label="联系邮箱" field="email" type="email" placeholder="contact@example.com" />
              <Field label="官方网站" field="website" placeholder="https://www.example.com" />
              <Field label="微信公众号" field="wechat" placeholder="微信公众号 ID" />
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">通讯地址</label>
                <div className="space-y-2">
                  <ChinaRegionPicker
                    value={regionFromNames(info.addressProvince, info.addressCity, info.addressDistrict)}
                    onChange={(region: ChinaRegionValue) => {
                      setInfo((p) => ({
                        ...p,
                        addressProvince: region.province,
                        addressCity: region.city,
                        addressDistrict: region.district,
                        address: composeChinaAddress(region.province, region.city, region.district, p.addressDetail),
                      }));
                    }}
                  />
                  <input
                    type="text"
                    value={info.addressDetail}
                    onChange={(e) => {
                      const detail = e.target.value;
                      setInfo((p) => ({
                        ...p,
                        addressDetail: detail,
                        address: composeChinaAddress(p.addressProvince, p.addressCity, p.addressDistrict, detail),
                      }));
                    }}
                    placeholder="街道、门牌号等详细地址"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>
              <Field label="微博账号" field="weibo" placeholder="@账号名" />
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-share-line text-red-500"></i> 社交媒体
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center bg-black rounded text-white text-xs"><i className="ri-tiktok-line text-xs"></i></span>
                  抖音号
                </label>
                <input
                  type="text"
                  value={info.douyin}
                  onChange={(e) => handleChange("douyin", e.target.value)}
                  placeholder="抖音账号 ID"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center bg-red-500 rounded text-white text-xs">小</span>
                  小红书账号
                </label>
                <input
                  type="text"
                  value={info.xiaohongshu}
                  onChange={(e) => handleChange("xiaohongshu", e.target.value)}
                  placeholder="小红书用户名"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center bg-sky-400 rounded text-white text-xs"><i className="ri-bilibili-line text-xs"></i></span>
                  Bilibili
                </label>
                <input
                  type="text"
                  value={info.bilibili}
                  onChange={(e) => handleChange("bilibili", e.target.value)}
                  placeholder="B站 UID 或 主页链接"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center bg-gray-800 rounded text-white text-xs"><i className="ri-twitter-x-line text-xs"></i></span>
                  Twitter / X
                </label>
                <input
                  type="text"
                  value={info.twitter}
                  onChange={(e) => handleChange("twitter", e.target.value)}
                  placeholder="@username"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Org Info */}
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-government-line text-red-500"></i> 组织信息
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">协会类型</label>
                <select
                  value={info.assocType}
                  onChange={(e) => handleChange("assocType", e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors bg-white"
                >
                  {["球迷协会", "球迷俱乐部", "球迷联盟", "官方认证球迷组织", "非营利组织", "其他"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">注册/备案编号</label>
                <input
                  type="text"
                  value={info.certNumber}
                  onChange={(e) => handleChange("certNumber", e.target.value)}
                  placeholder="如有，填写登记编号"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">覆盖城市/地区</label>
                <input
                  type="text"
                  value={info.coverCity}
                  onChange={(e) => handleChange("coverCity", e.target.value)}
                  placeholder="如：某某市"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
